import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import StatCard from '../../components/ui/StatCard';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import BarChart from '../../charts/bar';
import MyPayments from '../Payments/MyPayments';
import api from '../../api';
import { formatCurrency } from '../../utils/image';
import tokens from '../../theme/tokens';

const dataCurta = (data) => new Date(data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });

// Linha de lançamento: descrição, data, valor (+ verde / − vermelho) e, opcionalmente, o saldo após ele.
const Lancamento = ({ descricao, data, valor, saldo, abono }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: valor >= 0 ? tokens.color.successSoft : tokens.color.dangerSoft, color: valor >= 0 ? tokens.color.success : tokens.color.danger }}>
      {valor >= 0 ? <ArrowUpwardRoundedIcon fontSize="small" /> : <ArrowDownwardRoundedIcon fontSize="small" />}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>{descricao || 'Lançamento'}</Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography variant="caption" color="text.secondary">{dataCurta(data)}</Typography>
        {abono && <Chip size="small" label="Abono" variant="outlined" color="info" sx={{ height: 18 }} />}
      </Stack>
    </Box>
    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
      <Typography variant="body2" fontWeight={700} sx={{ color: valor >= 0 ? tokens.color.success : tokens.color.danger }}>
        {valor >= 0 ? '+' : '−'} {formatCurrency(Math.abs(valor))}
      </Typography>
      {saldo !== undefined && <Typography variant="caption" color="text.secondary">saldo {formatCurrency(saldo)}</Typography>}
    </Box>
  </Stack>
);

const ListaLancamentos = ({ itens, vazio, limite }) => {
  const [todos, setTodos] = useState(false);
  if (itens.length === 0) return <EmptyState title={vazio} />;
  const visiveis = todos || !limite ? itens : itens.slice(0, limite);
  return (
    <>
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {visiveis.map((item) => <Lancamento key={item._id} {...item} />)}
      </Stack>
      {limite && itens.length > limite && !todos && (
        <Button fullWidth onClick={() => setTodos(true)} sx={{ mt: 1 }}>Ver tudo ({itens.length})</Button>
      )}
    </>
  );
};

const SAUDE = {
  saudavel: { titulo: 'Saudável', cor: tokens.color.success, fundo: tokens.color.successSoft, icone: <CheckCircleRoundedIcon /> },
  atencao: { titulo: 'Atenção', cor: tokens.color.warning, fundo: tokens.color.warningSoft, icone: <WarningAmberRoundedIcon /> },
  critica: { titulo: 'Crítica', cor: tokens.color.danger, fundo: tokens.color.dangerSoft, icone: <ErrorRoundedIcon /> },
};

const Indicador = ({ label, valor, cor }) => (
  <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography fontWeight={700} sx={{ color: cor }}>{valor}</Typography>
  </Box>
);

// Saúde financeira da pelada: status, motivos e indicadores dos últimos 3 meses fechados.
const SaudeFinanceira = ({ saude }) => {
  const s = SAUDE[saude.status] || SAUDE.atencao;
  const cobertura = saude.cobertura === null ? 'Sem gastos' : `${saude.cobertura.toFixed(1).replace('.', ',')} meses`;
  return (
    <Card sx={{ p: 2.5, borderColor: s.cor, bgcolor: s.fundo }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: s.cor, display: 'grid', placeItems: 'center' }}>{s.icone}</Box>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>Saúde financeira da pelada</Typography>
          <Typography variant="h6" sx={{ color: s.cor, lineHeight: 1.2 }}>{s.titulo}</Typography>
        </Box>
      </Stack>
      <Stack component="ul" spacing={0.25} sx={{ m: 0, mb: 2, pl: 2.5 }}>
        {saude.motivos.map((motivo) => <Typography component="li" variant="body2" key={motivo}>{motivo}</Typography>)}
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
        <Indicador label="Média de entradas/mês" valor={formatCurrency(saude.mediaEntradas)} cor={tokens.color.success} />
        <Indicador label="Média de saídas/mês" valor={formatCurrency(saude.mediaSaidas)} cor={tokens.color.danger} />
        <Indicador label="Resultado médio/mês" valor={formatCurrency(saude.resultadoMedio)} cor={saude.resultadoMedio >= 0 ? tokens.color.success : tokens.color.danger} />
        <Indicador label="Cobertura do caixa" valor={cobertura} />
        <Indicador label="A receber (em atraso)" valor={`${formatCurrency(saude.inadimplencia.total)} · ${saude.inadimplencia.jogadores} jog.`} cor={saude.inadimplencia.jogadores ? tokens.color.warning : undefined} />
      </Stack>
    </Card>
  );
};

const corValor = (valor) => (valor > 0 ? tokens.color.success : valor < 0 ? tokens.color.danger : 'text.secondary');

// Tabela mês a mês (mais recente primeiro), com rolagem horizontal no celular.
const TabelaMeses = ({ meses }) => (
  <TableContainer sx={{ overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: 460 }}>
      <TableHead>
        <TableRow>
          <TableCell>Mês</TableCell>
          <TableCell align="right">Entradas</TableCell>
          <TableCell align="right">Saídas</TableCell>
          <TableCell align="right">Resultado</TableCell>
          <TableCell align="right">Saldo no fim</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...meses].reverse().map((m) => (
          <TableRow key={m.mes} hover>
            <TableCell sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{m.label}</TableCell>
            <TableCell align="right" sx={{ color: tokens.color.success }}>{formatCurrency(m.entradas)}</TableCell>
            <TableCell align="right" sx={{ color: tokens.color.danger }}>{formatCurrency(m.saidas)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: corValor(m.resultado) }}>{formatCurrency(m.resultado)}</TableCell>
            <TableCell align="right">{formatCurrency(m.saldoFinal)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Financeiro do jogador: meu saldo e extrato, e o caixa da pelada como um todo.
const FinanceiroPage = () => {
  const [aba, setAba] = useState(0);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    api
      .get('financeiro/resumo')
      .then((response) => setDados(response.data))
      .catch(() => setDados(null))
      .finally(() => setCarregando(false));
  }, []);

  const meuSaldo = dados?.meu.saldo || 0;
  const mesAtual = dados?.pelada.meses[dados.pelada.meses.length - 1];

  return (
    <AppLayout>
      <PageHeader eyebrow="Pelada Riviera" title="Financeiro" subtitle="Seu saldo e extrato, e o caixa da pelada." />

      {carregando ? (
        <LoadingState label="Carregando financeiro..." />
      ) : !dados ? (
        <Card><EmptyState title="Não foi possível carregar o financeiro" /></Card>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <StatCard
                label="Meu saldo"
                value={formatCurrency(meuSaldo)}
                hint={meuSaldo < 0 ? 'Em atraso: regularize para manter as prioridades na fila' : 'Em dia'}
                icon={<AccountBalanceWalletRoundedIcon />}
                tone={meuSaldo < 0 ? 'danger' : 'success'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                label="Saldo da pelada"
                value={formatCurrency(dados.pelada.saldo)}
                hint={mesAtual ? `Neste mês: +${formatCurrency(mesAtual.entradas)} · −${formatCurrency(mesAtual.saidas)}` : undefined}
                icon={<SavingsRoundedIcon />}
                tone={dados.pelada.saldo < 0 ? 'danger' : 'info'}
              />
            </Grid>
          </Grid>

          <Tabs value={aba} onChange={(e, v) => setAba(v)} variant="fullWidth" sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Tab label="Meu extrato" />
            <Tab label="Caixa da pelada" />
          </Tabs>

          {aba === 0 ? (
            <Stack spacing={3}>
              <SectionCard title="Meu extrato" subtitle="Cobranças (mensalidades e diárias) e pagamentos, do mais recente">
                <ListaLancamentos itens={dados.meu.extrato} vazio="Nenhum lançamento ainda" limite={15} />
              </SectionCard>
              <MyPayments />
            </Stack>
          ) : (
            <Stack spacing={3}>
              {dados.pelada.saude && <SaudeFinanceira saude={dados.pelada.saude} />}
              <SectionCard title="Entradas e saídas" subtitle={`Últimos ${dados.pelada.meses.length} meses`}>
                <BarChart
                  labels={dados.pelada.meses.map((m) => m.label)}
                  series={[
                    { label: 'Entradas', data: dados.pelada.meses.map((m) => m.entradas), color: tokens.color.success },
                    { label: 'Saídas', data: dados.pelada.meses.map((m) => m.saidas), color: tokens.color.brand },
                  ]}
                />
              </SectionCard>
              <SectionCard title="Mês a mês" subtitle="Entradas, saídas, resultado do mês e saldo do caixa no fim do mês">
                <TabelaMeses meses={dados.pelada.meses} />
              </SectionCard>
              <SectionCard title="Últimas movimentações" subtitle="Caixa do clube">
                <ListaLancamentos itens={dados.pelada.movimentos} vazio="Nenhuma movimentação no caixa" limite={10} />
              </SectionCard>
            </Stack>
          )}
        </>
      )}
    </AppLayout>
  );
};

export default FinanceiroPage;
